import React, { useState, useCallback, useEffect } from 'react';
import {
    ArrowLeft, Save, Play, MoreHorizontal, Trash2, Copy, Settings,
    Loader2, CheckCircle, AlertTriangle, Eye, Undo, Redo, HelpCircle, X,
    MousePointer, Move, Link2, Edit3, Lightbulb
} from 'lucide-react';
import FlowCanvas from './FlowCanvas';
import NodePalette from './NodePalette';
import NodeEditor from './NodeEditor';
import { NODE_TYPES } from './FlowNode';

// Onboarding Tutorial Component
const OnboardingTutorial = ({ onClose, onStartTour }) => {
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm">
            <div className="bg-[#0d1424] border border-white/10 rounded-2xl shadow-2xl max-w-lg w-full mx-4 overflow-hidden animate-fadeIn">
                <div className="p-6 border-b border-white/10">
                    <div className="flex items-center gap-3">
                        <div className="p-3 bg-[#ff4d4d]/20 rounded-xl">
                            <Lightbulb className="w-6 h-6 text-[#ff4d4d]" />
                        </div>
                        <div>
                            <h2 className="text-xl font-bold text-white">Welcome to Deal Flow Builder</h2>
                            <p className="text-sm text-white/50">Create conversation scripts to close deals</p>
                        </div>
                    </div>
                </div>

                <div className="p-6 space-y-4">
                    <div className="flex gap-4">
                        <div className="flex-shrink-0 w-10 h-10 rounded-full bg-emerald-500/20 flex items-center justify-center text-emerald-500 font-bold">
                            1
                        </div>
                        <div>
                            <h3 className="font-semibold text-white">Add Steps</h3>
                            <p className="text-sm text-white/60">Click buttons in the left panel to add conversation steps</p>
                        </div>
                    </div>

                    <div className="flex gap-4">
                        <div className="flex-shrink-0 w-10 h-10 rounded-full bg-sky-500/20 flex items-center justify-center text-sky-500 font-bold">
                            2
                        </div>
                        <div>
                            <h3 className="font-semibold text-white">Connect Steps</h3>
                            <p className="text-sm text-white/60">Click the <span className="text-[#ff4d4d]">colored circle</span> on the right of a step, then click the <span className="text-[#ff4d4d]">red circle</span> on the left of another step</p>
                        </div>
                    </div>

                    <div className="flex gap-4">
                        <div className="flex-shrink-0 w-10 h-10 rounded-full bg-violet-500/20 flex items-center justify-center text-violet-500 font-bold">
                            3
                        </div>
                        <div>
                            <h3 className="font-semibold text-white">Edit Steps</h3>
                            <p className="text-sm text-white/60">Click any step to select it, then edit its properties in the right panel</p>
                        </div>
                    </div>

                    <div className="flex gap-4">
                        <div className="flex-shrink-0 w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center text-amber-500 font-bold">
                            4
                        </div>
                        <div>
                            <h3 className="font-semibold text-white">Move Steps</h3>
                            <p className="text-sm text-white/60">Drag steps to reposition them on the canvas</p>
                        </div>
                    </div>
                </div>

                <div className="p-6 border-t border-white/10 bg-white/[0.02] flex gap-3">
                    <button
                        onClick={onClose}
                        className="flex-1 px-4 py-3 bg-white/10 hover:bg-white/15 text-white rounded-xl font-medium transition"
                    >
                        Skip Tutorial
                    </button>
                    <button
                        onClick={() => {
                            onStartTour();
                            onClose();
                        }}
                        className="flex-1 px-4 py-3 bg-[#ff4d4d] hover:bg-[#ff5c5c] text-white rounded-xl font-medium transition"
                    >
                        Got It!
                    </button>
                </div>
            </div>
        </div>
    );
};

// Help Panel Component
const HelpPanel = ({ onClose }) => {
    return (
        <div className="absolute bottom-16 right-4 z-40 w-80 bg-[#0d1424] border border-white/10 rounded-xl shadow-2xl overflow-hidden animate-fadeIn">
            <div className="p-4 border-b border-white/10 flex items-center justify-between">
                <h3 className="font-semibold text-white">Quick Help</h3>
                <button onClick={onClose} className="p-1 text-white/40 hover:text-white rounded">
                    <X size={16} />
                </button>
            </div>
            <div className="p-4 space-y-4 max-h-80 overflow-y-auto">
                <div className="space-y-3">
                    <h4 className="text-xs font-semibold text-white/40 uppercase tracking-wider">Mouse Actions</h4>
                    <div className="flex items-start gap-3">
                        <MousePointer size={16} className="text-[#ff4d4d] mt-0.5" />
                        <div>
                            <p className="text-sm text-white">Click step</p>
                            <p className="text-xs text-white/50">Select and edit properties</p>
                        </div>
                    </div>
                    <div className="flex items-start gap-3">
                        <Move size={16} className="text-[#ff4d4d] mt-0.5" />
                        <div>
                            <p className="text-sm text-white">Drag step</p>
                            <p className="text-xs text-white/50">Move on canvas</p>
                        </div>
                    </div>
                    <div className="flex items-start gap-3">
                        <Link2 size={16} className="text-[#ff4d4d] mt-0.5" />
                        <div>
                            <p className="text-sm text-white">Click output circle → Click input</p>
                            <p className="text-xs text-white/50">Connect two steps</p>
                        </div>
                    </div>
                </div>

                <div className="space-y-3">
                    <h4 className="text-xs font-semibold text-white/40 uppercase tracking-wider">Keyboard Shortcuts</h4>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                        <div className="flex items-center gap-2">
                            <kbd className="px-2 py-0.5 bg-white/10 rounded text-xs text-white/70">Ctrl+S</kbd>
                            <span className="text-white/60">Save</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <kbd className="px-2 py-0.5 bg-white/10 rounded text-xs text-white/70">Ctrl+Z</kbd>
                            <span className="text-white/60">Undo</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <kbd className="px-2 py-0.5 bg-white/10 rounded text-xs text-white/70">Delete</kbd>
                            <span className="text-white/60">Remove</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <kbd className="px-2 py-0.5 bg-white/10 rounded text-xs text-white/70">Esc</kbd>
                            <span className="text-white/60">Cancel</span>
                        </div>
                    </div>
                </div>

                <div className="space-y-3">
                    <h4 className="text-xs font-semibold text-white/40 uppercase tracking-wider">Canvas Controls</h4>
                    <div className="text-sm text-white/60 space-y-1">
                        <p>• Scroll to zoom in/out</p>
                        <p>• Hold Alt + drag to pan</p>
                        <p>• Click empty area to deselect</p>
                    </div>
                </div>
            </div>
        </div>
    );
};

const FlowBuilder = ({ flow, onSave, onBack, isSaving }) => {
    // Flow state
    const [flowName, setFlowName] = useState(flow?.name || 'Untitled Flow');
    const [flowDescription, setFlowDescription] = useState(flow?.description || '');
    const [nodes, setNodes] = useState(flow?.nodes || [
        {
            id: 'start_1',
            type: 'start',
            position: { x: 100, y: 200 },
            data: { title: 'Start', trigger: 'conversation_start' },
        }
    ]);
    const [connections, setConnections] = useState(flow?.connections || []);
    const [selectedNodeId, setSelectedNodeId] = useState(null);

    // History for undo/redo
    const [history, setHistory] = useState([]);
    const [historyIndex, setHistoryIndex] = useState(-1);

    // UI state
    const [showSettings, setShowSettings] = useState(false);
    const [hasChanges, setHasChanges] = useState(false);
    const [saveStatus, setSaveStatus] = useState('saved');
    const [showTutorial, setShowTutorial] = useState(() => {
        // Show tutorial for new flows
        return !flow?.id;
    });
    const [showHelp, setShowHelp] = useState(false);

    const selectedNode = nodes.find(n => n.id === selectedNodeId);

    // Save to history
    const saveToHistory = useCallback(() => {
        const state = { nodes: [...nodes], connections: [...connections] };
        setHistory(prev => [...prev.slice(0, historyIndex + 1), state]);
        setHistoryIndex(prev => prev + 1);
    }, [nodes, connections, historyIndex]);

    // Undo
    const undo = useCallback(() => {
        if (historyIndex > 0) {
            const prevState = history[historyIndex - 1];
            setNodes(prevState.nodes);
            setConnections(prevState.connections);
            setHistoryIndex(prev => prev - 1);
        }
    }, [history, historyIndex]);

    // Redo
    const redo = useCallback(() => {
        if (historyIndex < history.length - 1) {
            const nextState = history[historyIndex + 1];
            setNodes(nextState.nodes);
            setConnections(nextState.connections);
            setHistoryIndex(prev => prev + 1);
        }
    }, [history, historyIndex]);

    // Track changes
    useEffect(() => {
        setHasChanges(true);
        setSaveStatus('unsaved');
    }, [nodes, connections, flowName, flowDescription]);

    // Add node
    const handleAddNode = useCallback((type) => {
        saveToHistory();

        // Find a good position for the new node
        const maxX = Math.max(...nodes.map(n => n.position.x), 0);
        const avgY = nodes.length > 0
            ? nodes.reduce((sum, n) => sum + n.position.y, 0) / nodes.length
            : 200;

        const newNode = {
            id: `${type}_${Date.now()}`,
            type,
            position: { x: maxX + 320, y: avgY },
            data: { title: NODE_TYPES[type]?.label || type },
        };

        // Initialize condition branches
        if (type === 'condition') {
            newNode.data.branches = [
                { label: 'Yes', condition: 'contains "yes"' },
                { label: 'No', condition: '' },
            ];
        }

        setNodes(prev => [...prev, newNode]);
        setSelectedNodeId(newNode.id);
    }, [nodes, saveToHistory]);

    // Update node
    const handleNodeUpdate = useCallback((nodeId, updates) => {
        setNodes(prev => prev.map(node =>
            node.id === nodeId
                ? { ...node, ...updates, data: { ...node.data, ...updates.data } }
                : node
        ));
    }, []);

    // Delete node
    const handleNodeDelete = useCallback((nodeId) => {
        saveToHistory();

        // Don't delete start node
        const node = nodes.find(n => n.id === nodeId);
        if (node?.type === 'start') return;

        setNodes(prev => prev.filter(n => n.id !== nodeId));
        setConnections(prev => prev.filter(c => c.from !== nodeId && c.to !== nodeId));

        if (selectedNodeId === nodeId) {
            setSelectedNodeId(null);
        }
    }, [nodes, selectedNodeId, saveToHistory]);

    // Duplicate node
    const handleNodeDuplicate = useCallback((nodeId) => {
        saveToHistory();

        const node = nodes.find(n => n.id === nodeId);
        if (!node || node.type === 'start') return;

        const newNode = {
            ...node,
            id: `${node.type}_${Date.now()}`,
            position: {
                x: node.position.x + 40,
                y: node.position.y + 40,
            },
            data: { ...node.data, title: `${node.data?.title || NODE_TYPES[node.type]?.label} (copy)` },
        };

        setNodes(prev => [...prev, newNode]);
        setSelectedNodeId(newNode.id);
    }, [nodes, saveToHistory]);

    // Create connection
    const handleConnectionCreate = useCallback((connection) => {
        saveToHistory();
        setConnections(prev => [...prev, connection]);
    }, [saveToHistory]);

    // Delete connection
    const handleConnectionDelete = useCallback((connectionId) => {
        saveToHistory();
        setConnections(prev => prev.filter(c => c.id !== connectionId));
    }, [saveToHistory]);

    // Save flow
    const handleSave = async () => {
        setSaveStatus('saving');

        const flowData = {
            id: flow?.id,
            name: flowName,
            description: flowDescription,
            nodes,
            connections,
            status: flow?.status || 'draft',
        };

        try {
            await onSave?.(flowData);
            setSaveStatus('saved');
            setHasChanges(false);
        } catch (err) {
            console.error('Failed to save flow:', err);
            setSaveStatus('unsaved');
        }
    };

    // Keyboard shortcuts
    useEffect(() => {
        const handleKeyDown = (e) => {
            if ((e.ctrlKey || e.metaKey) && e.key === 's') {
                e.preventDefault();
                handleSave();
            }
            if ((e.ctrlKey || e.metaKey) && e.key === 'z') {
                e.preventDefault();
                if (e.shiftKey) redo();
                else undo();
            }
            if ((e.ctrlKey || e.metaKey) && e.key === 'y') {
                e.preventDefault();
                redo();
            }
            if (e.key === 'Delete' || e.key === 'Backspace') {
                if (selectedNodeId && !e.target.closest('input') && !e.target.closest('textarea')) {
                    handleNodeDelete(selectedNodeId);
                }
            }
            if (e.key === 'Escape') {
                setSelectedNodeId(null);
            }
            if (e.key === '?') {
                setShowHelp(prev => !prev);
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [selectedNodeId, handleSave, undo, redo, handleNodeDelete]);

    return (
        <div className="flex flex-col h-full bg-[#050810]">
            {/* Onboarding Tutorial */}
            {showTutorial && (
                <OnboardingTutorial
                    onClose={() => setShowTutorial(false)}
                    onStartTour={() => {}}
                />
            )}

            {/* Help Panel */}
            {showHelp && <HelpPanel onClose={() => setShowHelp(false)} />}

            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-white/10 bg-[#0d1424]">
                <div className="flex items-center gap-4">
                    <button
                        onClick={onBack}
                        className="p-2 text-white/50 hover:text-white rounded-lg hover:bg-white/5 transition"
                    >
                        <ArrowLeft size={20} />
                    </button>

                    <div>
                        <input
                            type="text"
                            value={flowName}
                            onChange={(e) => setFlowName(e.target.value)}
                            className="text-lg font-semibold text-white bg-transparent border-none outline-none focus:ring-0 w-64"
                            placeholder="Flow name..."
                        />
                        <div className="flex items-center gap-2 mt-0.5">
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                                flow?.status === 'active'
                                    ? 'bg-emerald-500/20 text-emerald-500'
                                    : 'bg-white/10 text-white/50'
                            }`}>
                                {flow?.status || 'Draft'}
                            </span>
                            {saveStatus === 'saving' && (
                                <span className="flex items-center gap-1 text-xs text-white/40">
                                    <Loader2 size={12} className="animate-spin" />
                                    Saving...
                                </span>
                            )}
                            {saveStatus === 'saved' && !hasChanges && (
                                <span className="flex items-center gap-1 text-xs text-emerald-500">
                                    <CheckCircle size={12} />
                                    Saved
                                </span>
                            )}
                            {saveStatus === 'unsaved' && hasChanges && (
                                <span className="flex items-center gap-1 text-xs text-amber-500">
                                    <AlertTriangle size={12} />
                                    Unsaved changes
                                </span>
                            )}
                        </div>
                    </div>
                </div>

                <div className="flex items-center gap-2">
                    {/* Undo/Redo */}
                    <div className="flex items-center bg-white/5 rounded-lg p-1">
                        <button
                            onClick={undo}
                            disabled={historyIndex <= 0}
                            className="p-2 text-white/50 hover:text-white rounded-lg hover:bg-white/5 transition disabled:opacity-30 disabled:hover:bg-transparent"
                            title="Undo (Ctrl+Z)"
                        >
                            <Undo size={18} />
                        </button>
                        <button
                            onClick={redo}
                            disabled={historyIndex >= history.length - 1}
                            className="p-2 text-white/50 hover:text-white rounded-lg hover:bg-white/5 transition disabled:opacity-30 disabled:hover:bg-transparent"
                            title="Redo (Ctrl+Y)"
                        >
                            <Redo size={18} />
                        </button>
                    </div>

                    <button
                        onClick={() => setShowHelp(!showHelp)}
                        className={`p-2 rounded-lg transition ${
                            showHelp
                                ? 'bg-[#ff4d4d]/20 text-[#ff4d4d]'
                                : 'text-white/50 hover:text-white hover:bg-white/5'
                        }`}
                        title="Help (?)"
                    >
                        <HelpCircle size={18} />
                    </button>

                    <button
                        onClick={() => setShowSettings(!showSettings)}
                        className="p-2 text-white/50 hover:text-white rounded-lg hover:bg-white/5 transition"
                        title="Settings"
                    >
                        <Settings size={18} />
                    </button>

                    <button
                        className="px-4 py-2 text-white/70 hover:text-white rounded-lg hover:bg-white/5 transition flex items-center gap-2"
                        title="Preview"
                    >
                        <Eye size={18} />
                        Preview
                    </button>

                    <button
                        onClick={handleSave}
                        disabled={isSaving || !hasChanges}
                        className="px-4 py-2 bg-[#ff4d4d] hover:bg-[#ff5c5c] text-white rounded-lg font-medium flex items-center gap-2 transition disabled:opacity-50"
                    >
                        {isSaving ? (
                            <>
                                <Loader2 size={18} className="animate-spin" />
                                Saving...
                            </>
                        ) : (
                            <>
                                <Save size={18} />
                                Save
                            </>
                        )}
                    </button>
                </div>
            </div>

            {/* Main Content */}
            <div className="flex-1 flex min-h-0">
                {/* Left Palette */}
                <NodePalette onAddNode={handleAddNode} />

                {/* Canvas */}
                <FlowCanvas
                    nodes={nodes}
                    connections={connections}
                    selectedNodeId={selectedNodeId}
                    onNodeSelect={setSelectedNodeId}
                    onNodeUpdate={handleNodeUpdate}
                    onNodeDelete={handleNodeDelete}
                    onNodeDuplicate={handleNodeDuplicate}
                    onConnectionCreate={handleConnectionCreate}
                    onConnectionDelete={handleConnectionDelete}
                />

                {/* Right Panel - Node Editor */}
                {selectedNode && (
                    <NodeEditor
                        node={selectedNode}
                        onUpdate={handleNodeUpdate}
                        onClose={() => setSelectedNodeId(null)}
                    />
                )}
            </div>

            {/* Stats Bar */}
            <div className="flex items-center justify-between px-4 py-2 border-t border-white/10 bg-[#0d1424]/50 text-xs text-white/40">
                <div className="flex items-center gap-4">
                    <span>{nodes.length} steps</span>
                    <span>{connections.length} connections</span>
                </div>
                <div className="flex items-center gap-4">
                    <button
                        onClick={() => setShowHelp(true)}
                        className="hover:text-white transition"
                    >
                        Press ? for help
                    </button>
                </div>
            </div>
        </div>
    );
};

export default FlowBuilder;
